import React from "react";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { intersection } from "lodash";
import ReactSelect, { components } from "react-select";
import { ClipLoader } from "react-spinners";
import { createConversation } from "../../../actions/conversations/createConversation";
import { fetchRecipients } from "../../../actions/recipients/fetchRecipients";

export const NewConversationModal = ({
  show,
  handleClose: onClose,
  recipients,
}) => {
  const dispatch = useDispatch();
  const { groups, categories } = useSelector((state) => state);
  const { can_message_by_category } = useSelector(
    (state) => state.organization
  );
  const { organization } = useSelector(
    (state) => state
  );
  const treatmentCenterGroups = groups.filter(
    (group) => group.kind == "treatment center"
  );
  const drugCourtProgramGroups = groups.filter(
    (group) => group.kind == "drug court program"
  );
  const kindOptions = React.useMemo(() => {
    let arr = [{ label: "By Recipient", value: "recipient" }];
    
    
    if (can_message_by_category && organization['kind']['affiliate'] === false)
      arr.push({ label: "By Category", value: "category" });
    return arr;
  }, [can_message_by_category]);

  const [kind, setKind] = React.useState(kindOptions[0]);
  const [selectedCategory, setSelectedCategory] = React.useState(null);
  const [selectedCategoryGroup, setSelectedCategoryGroup] =
    React.useState(null);
  const [selectedRecipients, setSelectedRecipients] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [body, setBody] = React.useState("");
  const [error, setError] = React.useState("");
  const [showConfirmationAlert, setShowConfirmationAlert] =
    React.useState(false);
  const [conversationTitle, setConversationTitle] = React.useState("");
  const [readOnly, setReadOnly] = React.useState(false);
  const [readOnlyMutable, setReadOnlyMutable] = React.useState(true);

  const categoryGroupOptions = groups.map((group) => ({
    label: group.name,
    value: group.id,
  }));
  React.useEffect(() => {
    if (show) dispatch(fetchRecipients());
  }, [show]);

  function markAsReadOnly(selectedOptions) {
    const clientOptions = selectedOptions.filter(
      (option) =>
        intersection(option.role_names, ["Client", "Alumni"]).length > 0
    );
    const treatmentCenterGroupNames = treatmentCenterGroups.map(
      (group) => group.name
    );
    const drugCourtProgramGroupNames = drugCourtProgramGroups.map(
      (group) => group.name
    );
    if (clientOptions.length > 1) {
      const shouldMarkAsReadOnly = clientOptions.reduce(
        (result = false, currentOption) => {
          if (result) return true;
          if (
            intersection(currentOption.group_names, treatmentCenterGroupNames, drugCourtProgramGroupNames)
              .length > 0
          )
            return true;
          return false;
        },
        false
      );

      if (shouldMarkAsReadOnly) {
        setReadOnly(true);
        setReadOnlyMutable(false);
        return true;
      } else {
        setReadOnly(false);
        setReadOnlyMutable(true);
      }
    } else {
      setReadOnly(false);
      setReadOnlyMutable(true);
    }

    return false;
  }

  function handleSetSelectedRecipients(options) {
    const isMarkedAsRead = markAsReadOnly(options);
    if (options.length == 0) return setSelectedRecipients(options);
    if (options.length < selectedRecipients.length)
      return setSelectedRecipients(options);
    let latestOption = options[options.length - 1];
    if (
      !allSelectedRecipientsShareFacilitiesWith(
        latestOption,
        selectedRecipients
      ) &&
      !isMarkedAsRead
    )
      setShowConfirmationAlert(true);
    setSelectedRecipients(options);
  }

  function handleClose() {
    setLoading(false);
    setShowConfirmationAlert(false);
    setSelectedRecipients([]);
    setBody("");
    setError("");
    setConversationTitle("");
    setReadOnlyMutable(true);
    setReadOnly(false);
    onClose();
  }

  function handleSubmit() {
    setLoading(true);
    dispatch(
      createConversation(
        selectedRecipients.map((rec) => rec.value),
        body,
        readOnly,
        conversationTitle,
        kind,
        selectedCategory ? selectedCategory.value : null,
        selectedCategoryGroup ? selectedCategoryGroup.value : null
      )
    )
      .then(() => {
        setLoading(false);
        handleClose();
      })
      .fail((error) => {
        setError(error.responseText);
        setLoading(false);
      });
  }

  function customRecipientOption(props) {
    return (
      <components.Option {...props}>
        <div className="d-flex justify-content-between p-2">
          <b> {props.data.label}</b>
          <span>
            {props.data.role_names.map((role) => (
              <span className="text-capitalize badge badge-primary mr-2">
                {role}
              </span>
            ))}
          </span>
        </div>
      </components.Option>
    );
  }

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <div className="pl-4 pr-4 pt-3 pb-3 card-header d-flex justify-content-between align-items-center">
        <>New Conversation</>
        <div className="btn btn-primary btn-sm" onClick={handleClose}>
          <div className="fas fa-times" />
        </div>
      </div>
      <div className="p-4">
        {showConfirmationAlert ? (
          <WarningAlert
            {...{
              setSelectedRecipients,
              selectedRecipients,
              setShowConfirmationAlert,
            }}
          />
        ) : (
          <>
            <div className="form-group">
              <label>Kind</label>
              <ReactSelect
                onChange={(option) => setKind(option)}
                value={kind}
                options={kindOptions}
              />
            </div>
            {kind.value == "recipient" && (
              <div className="form-group">
                <label>Select Recipients</label>
                <ReactSelect
                  isMulti
                  isClearable
                  components={{
                    Option: customRecipientOption,
                  }}
                  value={selectedRecipients}
                  onChange={handleSetSelectedRecipients}
                  placeholder="Type in the recipient name or scroll through the list..."
                  options={recipients}
                />
              </div>
            )}
            {kind.value == "category" && (
              <>
                <div className="form-group">
                  <label>Select Category</label>
                  <ReactSelect
                    isClearable
                    value={selectedCategory}
                    onChange={(option) => setSelectedCategory(option)}
                    placeholder="Type in the category or scroll through the list..."
                    options={categories}
                  />
                </div>
                <div className="form-group">
                  <label>Select Facility</label>
                  <ReactSelect
                    isClearable
                    value={selectedCategoryGroup}
                    onChange={(option) => setSelectedCategoryGroup(option)}
                    placeholder="Type in the facility or scroll through the list..."
                    options={categoryGroupOptions}
                  />
                </div>
              </>
            )}
            <div className="form-group">
              <label>Message</label>
              <input
                value={body}
                className="form-control"
                onKeyUp={(e) => {
                  if (e.keyCode === 13) handleSubmit();
                }}
                onChange={(e) => setBody(e.target.value)}
              />
            </div>
            {kind.value != "category" && (
              <div className="py-2">
                <input
                  checked={readOnly}
                  onChange={(e) => {
                    setReadOnly(e.target.checked);
                  }}
                  disabled={!readOnlyMutable}
                  type="checkbox"
                />
                <label>
                  &nbsp;Make this conversation read only to restrict clients
                  from replying
                </label>
              </div>
            )}
            {selectedRecipients.length > 1 && kind.value == "recipient" && (
              <div className="form-group">
                <label>
                  Conversation Name{" "}
                  <small className="text-danger">required</small>
                </label>
                <input
                  className="form-control"
                  onChange={(e) => setConversationTitle(e.target.value)}
                  value={conversationTitle}
                />
                <small>
                  Note: The conversation name will be visible to recipients
                </small>
              </div>
            )}
            {error && <div className="text-danger pt-3 pb-5">{error}</div>}
            <div className="text-right">
              <button
                onClick={handleSubmit}
                disabled={
                  loading ||
                  (kind.value == "category" &&
                    (selectedCategory == null ||
                      selectedCategoryGroup == null)) ||
                  (kind.value == "recipient" &&
                    selectedRecipients.length == 0) ||
                  body.length == 0 ||
                  (selectedRecipients.length > 1 && conversationTitle.length == 0)
                }
                className="btn btn-primary"
              >
                {loading ? <ClipLoader color="white" size={16} /> : "Send"}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

const WarningAlert = ({
  setSelectedRecipients,
  selectedRecipients,
  setShowConfirmationAlert,
}) => (
  <div className="text-danger">
    <b>Users not in the same facility.</b>
    <div className="pt-3">
      You are attempting to create a conversation between clients that are{" "}
      <b>NOT</b> in the same facility. By adding this client to this
      conversation, this client will be able to see the names and messages of
      everyone else in this conversion. If this is on purpose, please continue
      by pressing send. Otherwise, remove the client from this conversation
    </div>
    <div className="d-flex justify-content-between pt-4">
      <button
        onClick={() => {
          setSelectedRecipients(selectedRecipients.slice(0, -1));
          setShowConfirmationAlert(false);
        }}
        className="btn btn-outline-danger"
      >
        Cancel
      </button>
      <button
        onClick={() => setShowConfirmationAlert(false)}
        className="btn btn-danger"
      >
        Continue
      </button>
    </div>
  </div>
);

export function includesAny(arrA, arrB) {
  return arrA.filter((x) => arrB.includes(x)).length > 0;
}

export function allSelectedRecipientsShareFacilitiesWith(
  recipient,
  selectedRecipients
) {
  let result = true;
  selectedRecipients.some((selectedRecipient) => {
    if (isResident(recipient) && isResident(selectedRecipient)) {
      if (!includesAny(recipient.group_names, selectedRecipient.group_names))
        return (result = false);
    }
  });
  return result;
}

function isResident(recipient) {
  return recipient.role_names.find((role) =>
    ["Client", "Alumni", "Lead"].includes(role)
  );
}
